'use client'

import React from 'react'
import Image from 'next/image'

import { useSession } from '@rewardops/auth/src/client'
import { Program, type Logo } from '@rewardops/env'
import { cn, useLocale } from '@rewardops/ui'

import MemberDropdown from './member-dropdown'

function getLocalizedLogo(program: Program, language: string) {
  const logo = program?.theme?.logo
    ? program.theme.logo[language] ?? Object.values(program.theme.logo)[0]
    : null
  const logoMobile = program?.theme?.logoMobile
    ? program.theme.logoMobile[language] ??
      Object.values(program.theme.logoMobile)[0]
    : null

  return { logo, logoMobile }
}

const ProgramBanner: React.FC = () => {
  const { language } = useLocale()
  const { data: session, isLoading } = useSession()

  const program = session?.user?.program

  const { logo, logoMobile } = getLocalizedLogo(program as Program, language)

  if (isLoading) {
    return <div className="container"></div>
  }

  if (!program) return null

  return (
    <div
      className="container flex items-center justify-between"
      style={{ minHeight: '4.5rem' }}
    >
      <ProgramBannerImage logo={logo} logoMobile={logoMobile} />

      <div className="flex">
        {session ? <MemberDropdown session={session} /> : undefined}
      </div>
    </div>
  )
}

type ProgramBannerImageProps = {
  logo?: Logo | null
  logoMobile?: Logo | null
}

const ProgramBannerImage = ({ logo, logoMobile }: ProgramBannerImageProps) => {
  if (!logo && !logoMobile) return null

  logoMobile = logoMobile ?? logo

  return (
    <>
      {logoMobile && (
        <div className="block md:hidden">
          <ImageComponent logo={logoMobile} />
        </div>
      )}
      {logo && (
        <div className="hidden md:block">
          <ImageComponent logo={logo} />
        </div>
      )}
    </>
  )
}

const cleanDimensions = (dimension: number | string) =>
  typeof dimension === 'string' ? Number(dimension) : dimension

const ImageComponent = ({ logo }: { logo: Logo }) => {
  const { width, height, url } = logo

  if (!width || !height) {
    return <img src={url} alt="banner-logo" className="h-fit" />
  }

  const cleanHeight = cleanDimensions(height ?? 100)
  const cleanWidth = cleanDimensions(width ?? 100)

  // If height greater than 52px, calculate adjustment to maintain aspect ratio
  const ratio = cleanHeight > 52 ? 52 / cleanHeight : 1
  const adjustedWidth = Math.round(cleanWidth * ratio)
  const adjustedHeight = Math.round(cleanHeight * ratio)
  return (
    <Image
      alt="Program banner logo"
      className="max-h-[60px]"
      src={url}
      width={adjustedWidth}
      height={adjustedHeight}
    />
  )
}

export default ProgramBanner
